/**
 * Data for the pages rendered by `app/pages/(content)/[slug].vue`.
 * Every string below is an i18n key, resolved per locale at render time.
 *
 * Required per entry: `id`, `slug`, `breadcrumb`, `title`, `description`, `imageSrc`,
 * `uspsTitle`, `usps`.
 *
 * Optional per entry — omit the key entirely and the section is not rendered:
 *
 * @typedef {object} ContentPageFaq
 * @property {string} [title] i18n key for the section heading; omitted renders no heading.
 * @property {{ question: string, answer: string }[]} items i18n keys; `answer` may contain HTML.
 *
 * @typedef {object} ContentPageTestimonial
 * @property {string} [title] i18n key for the section heading.
 * @property {string} reviewId Trustpilot id of the review to quote, e.g. `'6a5fc919e86a87c2776ae615'`.
 *   Resolves the same review in every locale and survives a re-sync, which re-sorts the list.
 *   The quote's marked words come from the review's own `highlight` field — there is no prop
 *   for it, so the mark always belongs to the summary it marks.
 *
 * @typedef {object} ContentPageRelatedLink
 * @property {string} label i18n key for the link text.
 * @property {string} [description] i18n key for a short supporting line.
 * @property {string} [contentPageId] `id` of another entry in this array.
 * @property {string} [blogPostId] `id` of an entry in `app/lib/blog-posts`.
 * @property {string} [route] a `ROUTES` value or raw path, run through `localePath`.
 *   Exactly one of `contentPageId` / `blogPostId` / `route` per link.
 *
 * @typedef {object} ContentPageRelatedLinks
 * @property {string} [title] i18n key for the section heading.
 * @property {ContentPageRelatedLink[]} items
 *
 * All nine pages now carry all three; see the `wedding` entry below for the shape.
 *
 * Two conventions worth keeping when adding a page. Related-link labels reuse a key
 * that already exists in all five locales — a blog post's `title` or a sibling page's
 * `seo.title` — so the section is translated by construction and cannot drift from what
 * it points at. And a testimonial's `reviewId` is matched to the page's event type where
 * the bundle has one, so a wedding page quotes a wedding.
 *
 * `section-keys.spec.js` fails the build if a referenced key is missing from any locale,
 * if a link has more than one target, or if a quoted review id does not exist.
 */
export const contentPagesData = [
  {
    id: 'wedding',
    slug: 'content_pages.wedding.slug',
    breadcrumb: 'content_pages.wedding.breadcrumb',
    title: 'content_pages.wedding.title',
    description: 'content_pages.wedding.description',
    imageSrc: '/images/event-types/wedding.webp',
    uspsTitle: 'content_pages.wedding.usps.title',
    usps: [
      {
        title: 'content_pages.wedding.usp.personal.title',
        description: 'content_pages.wedding.usp.personal.description',
        imageSrc: '/images/content/wedding-usp-personal-2026.avif',
      },
      {
        title: 'content_pages.wedding.usp.slideshow.title',
        description: 'content_pages.wedding.usp.slideshow.description',
        imageSrc: '/images/content/wedding-usp-slideshow-2026.avif',
      },
      {
        title: 'content_pages.wedding.usp.after.title',
        description: 'content_pages.wedding.usp.after.description',
        imageSrc: '/images/content/wedding-usp-after-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.wedding.testimonial.title',
      reviewId: '65f6d339608360c3dd7415c1',
    },
    faq: {
      title: 'content_pages.wedding.faq.title',
      items: [
        { question: 'content_pages.wedding.faq.q1.question', answer: 'content_pages.wedding.faq.q1.answer' },
        { question: 'content_pages.wedding.faq.q2.question', answer: 'content_pages.wedding.faq.q2.answer' },
        { question: 'content_pages.wedding.faq.q3.question', answer: 'content_pages.wedding.faq.q3.answer' },
        { question: 'content_pages.wedding.faq.q4.question', answer: 'content_pages.wedding.faq.q4.answer' },
        { question: 'content_pages.wedding.faq.q5.question', answer: 'content_pages.wedding.faq.q5.answer' },
        { question: 'content_pages.wedding.faq.q6.question', answer: 'content_pages.wedding.faq.q6.answer' },
      ],
    },
    // Labels reuse keys that already exist in all 5 locales, so the section is
    // translated by construction and cannot drift from what it points at.
    relatedLinks: {
      title: 'content_pages.wedding.related_links.title',
      items: [
        { label: 'blog.posts.collect_wedding_guest_photos.title', blogPostId: 'collect_wedding_guest_photos' },
        { label: 'blog.posts.wedding_photo_sharing_no_app.title', blogPostId: 'wedding_photo_sharing_no_app' },
        { label: 'blog.posts.live_wedding_slideshow.title', blogPostId: 'live_wedding_slideshow' },
        { label: 'content_pages.wedding_anniversary.seo.title', contentPageId: 'wedding_anniversary' },
      ],
    },
  },
  {
    id: 'birthday',
    slug: 'content_pages.birthday.slug',
    breadcrumb: 'content_pages.birthday.breadcrumb',
    title: 'content_pages.birthday.title',
    description: 'content_pages.birthday.description',
    imageSrc: '/images/event-types/birthday.webp',
    uspsTitle: 'content_pages.birthday.usps.title',
    usps: [
      {
        title: 'content_pages.birthday.usp.personal.title',
        description: 'content_pages.birthday.usp.personal.description',
        imageSrc: '/images/content/birthday-usp-personal-2026.avif',
      },
      {
        title: 'content_pages.birthday.usp.slideshow.title',
        description: 'content_pages.birthday.usp.slideshow.description',
        imageSrc: '/images/content/wedding-usp-slideshow-2026.avif',
      },
      {
        title: 'content_pages.birthday.usp.after.title',
        description: 'content_pages.birthday.usp.after.description',
        imageSrc: '/images/content/birthday-usp-after-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.birthday.testimonial.title',
      reviewId: '674f1cc82d040b2d87c7b16d',
    },
    faq: {
      title: 'content_pages.birthday.faq.title',
      items: [
        { question: 'content_pages.birthday.faq.q1.question', answer: 'content_pages.birthday.faq.q1.answer' },
        { question: 'content_pages.birthday.faq.q2.question', answer: 'content_pages.birthday.faq.q2.answer' },
        { question: 'content_pages.birthday.faq.q3.question', answer: 'content_pages.birthday.faq.q3.answer' },
        { question: 'content_pages.birthday.faq.q4.question', answer: 'content_pages.birthday.faq.q4.answer' },
        { question: 'content_pages.birthday.faq.q5.question', answer: 'content_pages.birthday.faq.q5.answer' },
        { question: 'content_pages.birthday.faq.q6.question', answer: 'content_pages.birthday.faq.q6.answer' },
      ],
    },
    relatedLinks: {
      title: 'content_pages.birthday.related_links.title',
      items: [
        { label: 'blog.posts.collect_birthday_guest_photos.title', blogPostId: 'collect_birthday_guest_photos' },
        { label: 'blog.posts.private_party_photo_album.title', blogPostId: 'private_party_photo_album' },
        { label: 'blog.posts.event_photo_upload_no_app.title', blogPostId: 'event_photo_upload_no_app' },
        { label: 'content_pages.family_gatherings.seo.title', contentPageId: 'family_gatherings' },
      ],
    },
  },
  {
    id: 'christmas',
    slug: 'content_pages.christmas.slug',
    breadcrumb: 'content_pages.christmas.breadcrumb',
    title: 'content_pages.christmas.title',
    description: 'content_pages.christmas.description',
    imageSrc: '/images/event-types/christmas.webp',
    uspsTitle: 'content_pages.christmas.usps.title',
    usps: [
      {
        title: 'content_pages.christmas.usp.personal.title',
        description: 'content_pages.christmas.usp.personal.description',
        imageSrc: '/images/content/christmas-personal-2026.avif',
      },
      {
        title: 'content_pages.christmas.usp.slideshow.title',
        description: 'content_pages.christmas.usp.slideshow.description',
        imageSrc: '/images/content/christmas-slideshow-2026.avif',
      },
      {
        title: 'content_pages.christmas.usp.after.title',
        description: 'content_pages.christmas.usp.after.description',
        imageSrc: '/images/content/christmas-after-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.christmas.testimonial.title',
      reviewId: '665dd2d773b6bf604a9c6ab9',
    },
    faq: {
      title: 'content_pages.christmas.faq.title',
      items: [
        { question: 'content_pages.christmas.faq.q1.question', answer: 'content_pages.christmas.faq.q1.answer' },
        { question: 'content_pages.christmas.faq.q2.question', answer: 'content_pages.christmas.faq.q2.answer' },
        { question: 'content_pages.christmas.faq.q3.question', answer: 'content_pages.christmas.faq.q3.answer' },
        { question: 'content_pages.christmas.faq.q4.question', answer: 'content_pages.christmas.faq.q4.answer' },
        { question: 'content_pages.christmas.faq.q5.question', answer: 'content_pages.christmas.faq.q5.answer' },
        { question: 'content_pages.christmas.faq.q6.question', answer: 'content_pages.christmas.faq.q6.answer' },
      ],
    },
    relatedLinks: {
      title: 'content_pages.christmas.related_links.title',
      items: [
        { label: 'blog.posts.collect_corporate_event_photos.title', blogPostId: 'collect_corporate_event_photos' },
        { label: 'blog.posts.qr_code_event_photo_sharing.title', blogPostId: 'qr_code_event_photo_sharing' },
        { label: 'content_pages.company_party.seo.title', contentPageId: 'company_party' },
        { label: 'content_pages.new_years_eve.seo.title', contentPageId: 'new_years_eve' },
      ],
    },
  },
  {
    id: 'company_party',
    slug: 'content_pages.company_party.slug',
    breadcrumb: 'content_pages.company_party.breadcrumb',
    title: 'content_pages.company_party.title',
    description: 'content_pages.company_party.description',
    imageSrc: '/images/event-types/business-party.webp',
    uspsTitle: 'content_pages.company_party.usps.title',
    usps: [
      {
        title: 'content_pages.company_party.usp.personal.title',
        description: 'content_pages.company_party.usp.personal.description',
        imageSrc: '/images/content/personal-qr-2026.avif',
      },
      {
        title: 'content_pages.company_party.usp.slideshow.title',
        description: 'content_pages.company_party.usp.slideshow.description',
        imageSrc: '/images/content/slideshow-example-2026.avif',
      },
      {
        title: 'content_pages.company_party.usp.after.title',
        description: 'content_pages.company_party.usp.after.description',
        imageSrc: '/images/content/after-example-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.company_party.testimonial.title',
      reviewId: '676aaa35c2d43f032119d43a',
    },
    faq: {
      title: 'content_pages.company_party.faq.title',
      items: [
        {
          question: 'content_pages.company_party.faq.q1.question',
          answer: 'content_pages.company_party.faq.q1.answer',
        },
        {
          question: 'content_pages.company_party.faq.q2.question',
          answer: 'content_pages.company_party.faq.q2.answer',
        },
        {
          question: 'content_pages.company_party.faq.q3.question',
          answer: 'content_pages.company_party.faq.q3.answer',
        },
        {
          question: 'content_pages.company_party.faq.q4.question',
          answer: 'content_pages.company_party.faq.q4.answer',
        },
        {
          question: 'content_pages.company_party.faq.q5.question',
          answer: 'content_pages.company_party.faq.q5.answer',
        },
        {
          question: 'content_pages.company_party.faq.q6.question',
          answer: 'content_pages.company_party.faq.q6.answer',
        },
        {
          question: 'content_pages.company_party.faq.q7.question',
          answer: 'content_pages.company_party.faq.q7.answer',
        },
      ],
    },
    relatedLinks: {
      title: 'content_pages.company_party.related_links.title',
      items: [
        { label: 'blog.posts.collect_corporate_event_photos.title', blogPostId: 'collect_corporate_event_photos' },
        { label: 'blog.posts.qr_code_event_photo_sharing.title', blogPostId: 'qr_code_event_photo_sharing' },
        { label: 'blog.posts.best_company_party_gallery.title', blogPostId: 'best_company_party_gallery' },
        { label: 'content_pages.christmas.seo.title', contentPageId: 'christmas' },
      ],
    },
  },
  {
    id: 'new_years_eve',
    slug: 'content_pages.new_years_eve.slug',
    breadcrumb: 'content_pages.new_years_eve.breadcrumb',
    title: 'content_pages.new_years_eve.title',
    description: 'content_pages.new_years_eve.description',
    imageSrc: '/images/event-types/new_years_eve.webp',
    uspsTitle: 'content_pages.new_years_eve.usps.title',
    usps: [
      {
        title: 'content_pages.new_years_eve.usp.personal.title',
        description: 'content_pages.new_years_eve.usp.personal.description',
        imageSrc: '/images/content/new-years-eve-personal-2026.avif',
      },
      {
        title: 'content_pages.new_years_eve.usp.slideshow.title',
        description: 'content_pages.new_years_eve.usp.slideshow.description',
        imageSrc: '/images/content/new-years-eve-slideshow-2026.avif',
      },
      {
        title: 'content_pages.new_years_eve.usp.after.title',
        description: 'content_pages.new_years_eve.usp.after.description',
        imageSrc: '/images/content/new-years-eve-after-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.new_years_eve.testimonial.title',
      reviewId: '6a4b8cac33145a22726003ef',
    },
    faq: {
      title: 'content_pages.new_years_eve.faq.title',
      items: [
        {
          question: 'content_pages.new_years_eve.faq.q1.question',
          answer: 'content_pages.new_years_eve.faq.q1.answer',
        },
        {
          question: 'content_pages.new_years_eve.faq.q2.question',
          answer: 'content_pages.new_years_eve.faq.q2.answer',
        },
        {
          question: 'content_pages.new_years_eve.faq.q3.question',
          answer: 'content_pages.new_years_eve.faq.q3.answer',
        },
        {
          question: 'content_pages.new_years_eve.faq.q4.question',
          answer: 'content_pages.new_years_eve.faq.q4.answer',
        },
        {
          question: 'content_pages.new_years_eve.faq.q5.question',
          answer: 'content_pages.new_years_eve.faq.q5.answer',
        },
        {
          question: 'content_pages.new_years_eve.faq.q6.question',
          answer: 'content_pages.new_years_eve.faq.q6.answer',
        },
      ],
    },
    relatedLinks: {
      title: 'content_pages.new_years_eve.related_links.title',
      items: [
        { label: 'blog.posts.event_albums_guide.title', blogPostId: 'event_albums_guide' },
        { label: 'blog.posts.qr_code_event_photo_sharing.title', blogPostId: 'qr_code_event_photo_sharing' },
        { label: 'content_pages.christmas.seo.title', contentPageId: 'christmas' },
        { label: 'content_pages.company_party.seo.title', contentPageId: 'company_party' },
      ],
    },
  },
  {
    id: 'family_gatherings',
    slug: 'content_pages.family_gatherings.slug',
    breadcrumb: 'content_pages.family_gatherings.breadcrumb',
    title: 'content_pages.family_gatherings.title',
    description: 'content_pages.family_gatherings.description',
    imageSrc: '/images/event-types/family_gatherings.webp',
    uspsTitle: 'content_pages.family_gatherings.usps.title',
    usps: [
      {
        title: 'content_pages.family_gatherings.usp.personal.title',
        description: 'content_pages.family_gatherings.usp.personal.description',
        imageSrc: '/images/content/personal-qr-2026.avif',
      },
      {
        title: 'content_pages.family_gatherings.usp.slideshow.title',
        description: 'content_pages.family_gatherings.usp.slideshow.description',
        imageSrc: '/images/content/after-example-2026.avif',
      },
      {
        title: 'content_pages.family_gatherings.usp.after.title',
        description: 'content_pages.family_gatherings.usp.after.description',
        imageSrc: '/images/content/family-after-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.family_gatherings.testimonial.title',
      reviewId: '66629acbff306ecef66b3c12',
    },
    faq: {
      title: 'content_pages.family_gatherings.faq.title',
      items: [
        {
          question: 'content_pages.family_gatherings.faq.q1.question',
          answer: 'content_pages.family_gatherings.faq.q1.answer',
        },
        {
          question: 'content_pages.family_gatherings.faq.q2.question',
          answer: 'content_pages.family_gatherings.faq.q2.answer',
        },
        {
          question: 'content_pages.family_gatherings.faq.q3.question',
          answer: 'content_pages.family_gatherings.faq.q3.answer',
        },
        {
          question: 'content_pages.family_gatherings.faq.q4.question',
          answer: 'content_pages.family_gatherings.faq.q4.answer',
        },
        {
          question: 'content_pages.family_gatherings.faq.q5.question',
          answer: 'content_pages.family_gatherings.faq.q5.answer',
        },
        {
          question: 'content_pages.family_gatherings.faq.q6.question',
          answer: 'content_pages.family_gatherings.faq.q6.answer',
        },
      ],
    },
    relatedLinks: {
      title: 'content_pages.family_gatherings.related_links.title',
      items: [
        { label: 'blog.posts.private_party_photo_album.title', blogPostId: 'private_party_photo_album' },
        { label: 'blog.posts.event_albums_guide.title', blogPostId: 'event_albums_guide' },
        { label: 'blog.posts.event_photo_upload_no_app.title', blogPostId: 'event_photo_upload_no_app' },
        { label: 'content_pages.wedding_anniversary.seo.title', contentPageId: 'wedding_anniversary' },
      ],
    },
  },
  {
    id: 'sports_events',
    slug: 'content_pages.sports_events.slug',
    breadcrumb: 'content_pages.sports_events.breadcrumb',
    title: 'content_pages.sports_events.title',
    description: 'content_pages.sports_events.description',
    imageSrc: '/images/event-types/sport-event.webp',
    uspsTitle: 'content_pages.sports_events.usps.title',
    usps: [
      {
        title: 'content_pages.sports_events.usp.personal.title',
        description: 'content_pages.sports_events.usp.personal.description',
        imageSrc: '/images/content/personal-qr-2026.avif',
      },
      {
        title: 'content_pages.sports_events.usp.slideshow.title',
        description: 'content_pages.sports_events.usp.slideshow.description',
        imageSrc: '/images/content/slideshow-example-2026.avif',
      },
      {
        title: 'content_pages.sports_events.usp.after.title',
        description: 'content_pages.sports_events.usp.after.description',
        imageSrc: '/images/content/after-example-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.sports_events.testimonial.title',
      reviewId: '66e814b10cddfd7fe9a03eb5',
    },
    faq: {
      title: 'content_pages.sports_events.faq.title',
      items: [
        {
          question: 'content_pages.sports_events.faq.q1.question',
          answer: 'content_pages.sports_events.faq.q1.answer',
        },
        {
          question: 'content_pages.sports_events.faq.q2.question',
          answer: 'content_pages.sports_events.faq.q2.answer',
        },
        {
          question: 'content_pages.sports_events.faq.q3.question',
          answer: 'content_pages.sports_events.faq.q3.answer',
        },
        {
          question: 'content_pages.sports_events.faq.q4.question',
          answer: 'content_pages.sports_events.faq.q4.answer',
        },
        {
          question: 'content_pages.sports_events.faq.q5.question',
          answer: 'content_pages.sports_events.faq.q5.answer',
        },
        {
          question: 'content_pages.sports_events.faq.q6.question',
          answer: 'content_pages.sports_events.faq.q6.answer',
        },
      ],
    },
    relatedLinks: {
      title: 'content_pages.sports_events.related_links.title',
      items: [
        { label: 'blog.posts.share_team_photos_sports_event.title', blogPostId: 'share_team_photos_sports_event' },
        { label: 'blog.posts.event_albums_guide.title', blogPostId: 'event_albums_guide' },
        { label: 'blog.posts.qr_code_video_upload.title', blogPostId: 'qr_code_video_upload' },
        { label: 'content_pages.company_party.seo.title', contentPageId: 'company_party' },
      ],
    },
  },
  {
    id: 'vacation',
    slug: 'content_pages.vacation.slug',
    breadcrumb: 'content_pages.vacation.breadcrumb',
    title: 'content_pages.vacation.title',
    description: 'content_pages.vacation.description',
    imageSrc: '/images/event-types/vacation.webp',
    uspsTitle: 'content_pages.vacation.usps.title',
    usps: [
      {
        title: 'content_pages.vacation.usp.personal.title',
        description: 'content_pages.vacation.usp.personal.description',
        imageSrc: '/images/content/vacation-personal-2026.avif',
      },
      {
        title: 'content_pages.vacation.usp.slideshow.title',
        description: 'content_pages.vacation.usp.slideshow.description',
        imageSrc: '/images/content/vacation-slideshow-2026.avif',
      },
      {
        title: 'content_pages.vacation.usp.after.title',
        description: 'content_pages.vacation.usp.after.description',
        imageSrc: '/images/content/vacation-after-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.vacation.testimonial.title',
      // No holiday-type review exists in the bundle, so a generally worded one.
      reviewId: '682c6737a4a060c607d6e83a',
    },
    faq: {
      title: 'content_pages.vacation.faq.title',
      items: [
        { question: 'content_pages.vacation.faq.q1.question', answer: 'content_pages.vacation.faq.q1.answer' },
        { question: 'content_pages.vacation.faq.q2.question', answer: 'content_pages.vacation.faq.q2.answer' },
        { question: 'content_pages.vacation.faq.q3.question', answer: 'content_pages.vacation.faq.q3.answer' },
        { question: 'content_pages.vacation.faq.q4.question', answer: 'content_pages.vacation.faq.q4.answer' },
        { question: 'content_pages.vacation.faq.q5.question', answer: 'content_pages.vacation.faq.q5.answer' },
        { question: 'content_pages.vacation.faq.q6.question', answer: 'content_pages.vacation.faq.q6.answer' },
      ],
    },
    relatedLinks: {
      title: 'content_pages.vacation.related_links.title',
      items: [
        { label: 'blog.posts.event_albums_guide.title', blogPostId: 'event_albums_guide' },
        { label: 'blog.posts.qr_code_video_upload.title', blogPostId: 'qr_code_video_upload' },
        { label: 'blog.posts.eu_data_security.title', blogPostId: 'eu_data_security' },
        { label: 'content_pages.family_gatherings.seo.title', contentPageId: 'family_gatherings' },
      ],
    },
  },
  {
    id: 'wedding_anniversary',
    slug: 'content_pages.wedding_anniversary.slug',
    breadcrumb: 'content_pages.wedding_anniversary.breadcrumb',
    title: 'content_pages.wedding_anniversary.title',
    description: 'content_pages.wedding_anniversary.description',
    imageSrc: '/images/event-types/anniversary.webp',
    uspsTitle: 'content_pages.wedding_anniversary.usps.title',
    usps: [
      {
        title: 'content_pages.wedding_anniversary.usp.personal.title',
        description: 'content_pages.wedding_anniversary.usp.personal.description',
        imageSrc: '/images/content/wedding-usp-personal-2026.avif',
      },
      {
        title: 'content_pages.wedding_anniversary.usp.slideshow.title',
        description: 'content_pages.wedding_anniversary.usp.slideshow.description',
        imageSrc: '/images/content/wedding-usp-slideshow-2026.avif',
      },
      {
        title: 'content_pages.wedding_anniversary.usp.after.title',
        description: 'content_pages.wedding_anniversary.usp.after.description',
        imageSrc: '/images/content/wedding-usp-after-2026.avif',
      },
    ],
    testimonial: {
      title: 'content_pages.wedding_anniversary.testimonial.title',
      reviewId: '68bff15e93aeb6b0ccdc351b',
    },
    faq: {
      title: 'content_pages.wedding_anniversary.faq.title',
      items: [
        {
          question: 'content_pages.wedding_anniversary.faq.q1.question',
          answer: 'content_pages.wedding_anniversary.faq.q1.answer',
        },
        {
          question: 'content_pages.wedding_anniversary.faq.q2.question',
          answer: 'content_pages.wedding_anniversary.faq.q2.answer',
        },
        {
          question: 'content_pages.wedding_anniversary.faq.q3.question',
          answer: 'content_pages.wedding_anniversary.faq.q3.answer',
        },
        {
          question: 'content_pages.wedding_anniversary.faq.q4.question',
          answer: 'content_pages.wedding_anniversary.faq.q4.answer',
        },
        {
          question: 'content_pages.wedding_anniversary.faq.q5.question',
          answer: 'content_pages.wedding_anniversary.faq.q5.answer',
        },
        {
          question: 'content_pages.wedding_anniversary.faq.q6.question',
          answer: 'content_pages.wedding_anniversary.faq.q6.answer',
        },
      ],
    },
    relatedLinks: {
      title: 'content_pages.wedding_anniversary.related_links.title',
      items: [
        { label: 'blog.posts.anniversary_party_memories.title', blogPostId: 'anniversary_party_memories' },
        { label: 'blog.posts.collect_wedding_guest_photos.title', blogPostId: 'collect_wedding_guest_photos' },
        { label: 'blog.posts.qr_code_event_photo_sharing.title', blogPostId: 'qr_code_event_photo_sharing' },
        { label: 'content_pages.wedding.seo.title', contentPageId: 'wedding' },
      ],
    },
  },
]

/**
 * Related-link items for `ContentRelatedLinks`, built from content page ids.
 *
 * The label reuses each page's own SEO title, so a link to a commercial page needs
 * no new strings and is translated in all five locales by construction. No
 * description: the nine SEO descriptions are one sentence apart from each other
 * ("… van je <event> op één plek"), and stacked three deep they read as filler.
 * Unknown ids are dropped rather than rendered as a dead link.
 *
 * @param {string[]} ids `id` values from `contentPagesData`, most relevant first.
 * @returns {{ label: string, contentPageId: string }[]}
 */
export function buildContentPageLinks(ids = []) {
  return ids
    .filter((id) => contentPagesData.some((contentPage) => contentPage.id === id))
    .map((id) => ({
      label: `content_pages.${id}.seo.title`,
      contentPageId: id,
    }))
}
